import type { Snippet } from '@/types';

/**
 * SnippetStorage — persists the clips library.
 *
 * v1 is localStorage-only (docs/design/SNIPPETS-SPEC.md "Persistence"): the
 * shared `tortoise` Supabase project is multi-app infra, and snippets don't
 * get a table without an explicit infra decision. Shaped after
 * progression-storage.ts's ProgressionStorage (async CRUD over a JSON array
 * keyed in localStorage) so a cloud path can be dropped in behind the same
 * methods later without callers changing. See troth idea filed for cloud
 * sync instead of building it here.
 */
class SnippetStorage {
  private readonly LOCAL_STORAGE_KEY = 'neume-snippets';

  private getFromLocalStorage(): Snippet[] {
    const data = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  private writeToLocalStorage(snippets: Snippet[]): void {
    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(snippets));
  }

  async save(snippet: Snippet): Promise<void> {
    const snippets = this.getFromLocalStorage();
    const index = snippets.findIndex((s) => s.id === snippet.id);

    if (index >= 0) {
      snippets[index] = snippet;
    } else {
      snippets.push(snippet);
    }

    this.writeToLocalStorage(snippets);
  }

  async getAll(): Promise<Snippet[]> {
    return this.getFromLocalStorage();
  }

  async getById(id: string): Promise<Snippet | null> {
    return this.getFromLocalStorage().find((s) => s.id === id) || null;
  }

  async delete(id: string): Promise<void> {
    const snippets = this.getFromLocalStorage().filter((s) => s.id !== id);
    this.writeToLocalStorage(snippets);
  }
}

// Export singleton instance, mirroring progressionStorage.
export const snippetStorage = new SnippetStorage();
